import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '../../../../../../../../redux/connect';
import { getPushpinThumbnailData, isLoadingThumbnails, getCurrentViewType } from '../../../../../../../../redux/selectors';
import { changesetType } from '../../../../../../../../constants/proptype-validation';
import ChannelItemBase from '../../../../../../../ChannelItemBase';
import ChangesetDetailsPushPinChange from './components/ChangesetDetailsPushPinChange';
import T from 'i18n-react';
import classNames from 'classnames';
import lodashGet from 'lodash/get';
import { toLocalDateTime } from '../../../../../../../../utils/date-helpers';
import { currentDocumentThumbnailType } from '../../../../../../../../constants/proptype-validation/issue-proptypes';
import { localizeChangesetDisplayNames } from '../../../../../../../../utils/changesets-helpers';
import { viewTypes } from '../../../../../../../../constants/view-types';
import ChangesetDescription from "./components/ChangesetDescription";

// eslint-disable-next-line react/prop-types
const Answer = ({ answer, isNew = false } = {}) => {
  if (answer) {
    const blockquoteClasses = {
      'ChangesetDetails__answer': true,
      [`ChangesetDetails__answer--${isNew ? 'new' : 'old'}`]: true,
    };
    return (
      <blockquote className={classNames(blockquoteClasses)} data-testid="ChangesetDetails__answer">
        {answer}
      </blockquote>
    );
  }
  return null;
};
Answer.displayName = 'Answer';

// eslint-disable-next-line react/prop-types
const CustomIdentifier = ({ customIdentifier } = {}) => {
  if (customIdentifier) {
    return (
      <blockquote className="ChangesetDetails__custom-identifier" data-testid="ChangesetDetails__custom-identifier">
        {T.translate("changeset.update.statusWithCustomIdentifier", { customIdentifier })}
      </blockquote>
    );
  }
  return null;
};
CustomIdentifier.displayName = 'CustomIdentifier';

const SubTextBody = ({ i18nParams, changes }) => {
  const { answerNew, officialResponseNew, customIdentifierNew, statusNew } = i18nParams;
  if (answerNew || officialResponseNew) {
    return (
      <Answer answer={answerNew || officialResponseNew} isNew={changes.isLastOfficialResponse} />
    );
  } else if (statusNew && customIdentifierNew) {
    return (
      <CustomIdentifier customIdentifier={customIdentifierNew} />
    );
  } else {
    return null;
  }
};
SubTextBody.displayName = 'SubTextBody';

const ChangesetBody = ({ i18nParams, i18nKey, isPushpinChange, changes, isLoadingThumbnails, changesetDocumentName, changesetDocumentThumbnail } = {}) => {
  const changesetHasDescription = !!i18nKey;
  const description = <ChangesetDescription i18nKey={i18nKey} i18nParams={i18nParams} />;

  if (isPushpinChange) {
    const thumbnailUrl = lodashGet(changesetDocumentThumbnail, 'attachmentUrl');
    const isDeletePushpin = !!lodashGet(changes, 'linkedDocument[0]');

    return (
      <ChangesetDetailsPushPinChange
        isLoadingThumbnails={isLoadingThumbnails}
        isDeletePushpin={isDeletePushpin}
        thumbnailUrl={thumbnailUrl}
        documentName={changesetDocumentName}
        description={description}
      />
    );
  }

  if (changesetHasDescription) {
    return (
      <div>
        <div>{description}</div>
        <SubTextBody i18nParams={i18nParams} changes={changes} />
      </div>
    );
  }

  return null;
};
ChangesetBody.displayName = 'ChangesetBody';
ChangesetBody.propTypes = {
  i18nParams: PropTypes.object,
  i18nKey: PropTypes.string,
  isPushpinChange: PropTypes.object,
  changes: PropTypes.object,
  isLoadingThumbnails: PropTypes.bool,
  changesetDocumentName: PropTypes.string,
  changesetDocumentThumbnail: PropTypes.object,
};

const OnBehalfOf = ({ showOnBehalfOf, onBehalfOfDisplayName }) => {
  if (!showOnBehalfOf) {
    return null;
  }
  return <span className="isui-channel-item__on-behalf-of">{T.translate('changeset.onBehalfOf', { name: onBehalfOfDisplayName })}</span>;
};
OnBehalfOf.propTypes = {
  showOnBehalfOf: PropTypes.bool,
  onBehalfOfDisplayName: PropTypes.string,
};
OnBehalfOf.displayName = 'OnBehalfOf';

export class PureChangesetDetails extends ChannelItemBase {

  static displayName = 'ChangesetDetails';

  static propTypes = {
    changeset: changesetType.isRequired,
    changesetDocumentThumbnail: currentDocumentThumbnailType,
    isLoadingThumbnails: PropTypes.bool,
    viewType: PropTypes.string,
  };

  render() {
    const { changeset, changesetDocumentName, changesetDocumentThumbnail, isLoadingThumbnails, viewType } = this.props;
    const { createdAt, createdByDisplayName, changesWithOxygen, isLastOfficialResponse, onBehalfOfDisplayName } = changeset;
    const changes = localizeChangesetDisplayNames({ ...changesWithOxygen, isLastOfficialResponse });
    // BE returns OnBehalfOf for any kind of changeset, but we want to display it only on changeset if type answer
    const showOnBehalfOf = !!changes['answer'] && onBehalfOfDisplayName;
    const { i18nParams, i18nKey, isPushpinChange } = changeset.changesetClientAttributes;

    return (
      <div>
        <header className="isui-channel-item__header">
          <div className="isui-channel-item__name">
            {createdByDisplayName || T.translate('userUndefined')}
            <OnBehalfOf
              showOnBehalfOf={showOnBehalfOf && viewType !== viewTypes.DOCUMENT}
              onBehalfOfDisplayName={onBehalfOfDisplayName}
            />
          </div>
          <span className="isui-channel-item__timestamp">{toLocalDateTime(createdAt)}</span>
        </header>
        <div className="isui-channel-item__body">
          <OnBehalfOf
            showOnBehalfOf={showOnBehalfOf && viewType === viewTypes.DOCUMENT}
            onBehalfOfDisplayName={onBehalfOfDisplayName}
          />
          <ChangesetBody
            i18nParams={i18nParams}
            i18nKey={i18nKey}
            isPushpinChange={isPushpinChange}
            changesetDocumentThumbnail={changesetDocumentThumbnail}
            changesetDocumentName={changesetDocumentName}
            isLoadingThumbnails={isLoadingThumbnails}
            changes={changes}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const {
    changesetDocumentThumbnail,
    changesetDocumentName } = getPushpinThumbnailData(props.changeset)(state);

  return {
    changesetDocumentName,
    changesetDocumentThumbnail,
    isLoadingThumbnails: isLoadingThumbnails(state),
    viewType: getCurrentViewType(state),
  };
};

export default connect(mapStateToProps)(PureChangesetDetails);
